import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Smile,
  Paperclip,
  Send,
  Mic,
  Image,
  FileText,
  MapPin,
  X,
  Camera,
  Globe,
  ExternalLink,
  Bot,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { i18n } from "../../../translate/i18n";

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

interface LinkPreviewData {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  url: string;
  loading: boolean;
  error: boolean;
}

// Função para extrair URL do texto
const extractUrl = (text: string): string | null => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = text.match(urlRegex);
  return matches ? matches[0] : null;
};

export function MessageInput({ onSendMessage, disabled = false }: MessageInputProps) {
  const [message, setMessage] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [linkPreview, setLinkPreview] = useState<LinkPreviewData | null>(null);
  const [lastCheckedUrl, setLastCheckedUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Detectar e buscar preview do link
  useEffect(() => {
    const url = extractUrl(message);
    
    // Se não tem URL, limpa o preview
    if (!url) {
      setLinkPreview(null);
      setLastCheckedUrl(null);
      return;
    }
    
    // Se é a mesma URL, não busca de novo
    if (url === lastCheckedUrl) return;
    
    setLastCheckedUrl(url);
    setLinkPreview({ url, loading: true, error: false });

    const fetchPreview = async () => {
      try {
        const response = await fetch(
          `https://api.microlink.io?url=${encodeURIComponent(url)}`
        );
        const data = await response.json();
        
        if (data.status === 'success') {
          setLinkPreview({
            title: data.data.title,
            description: data.data.description,
            image: data.data.image?.url,
            siteName: data.data.publisher,
            url,
            loading: false,
            error: false,
          });
        } else {
          setLinkPreview({ url, loading: false, error: true });
        }
      } catch (err) {
        setLinkPreview({ url, loading: false, error: true });
      }
    };

    // Debounce de 500ms para não fazer muitas requisições
    const timer = setTimeout(fetchPreview, 500);
    return () => clearTimeout(timer);
  }, [message, lastCheckedUrl]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message.trim());
      setMessage("");
      setLinkPreview(null);
      setLastCheckedUrl(null);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const removeLinkPreview = () => {
    setLinkPreview(null);
    setLastCheckedUrl(message); // Previne re-fetch
  };

  const quickEmojis = ["👍", "❤️", "😊", "🙏", "👏", "🎉", "✅", "🔥"];

  const attachmentOptions = [
    { icon: Image, label: i18n.t("v2.messageInput.attachments.image"), color: "#22C55E" },
    { icon: FileText, label: i18n.t("v2.messageInput.attachments.document"), color: "#2563EB" },
    { icon: Camera, label: i18n.t("v2.messageInput.attachments.camera"), color: "#8B5CF6" },
    { icon: MapPin, label: i18n.t("v2.messageInput.attachments.location"), color: "#EF4444" },
  ];

  // Se IA estiver ativa, mostra mensagem travada
  if (disabled) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative border-t border-[var(--border-color)] bg-[var(--card-bg)] px-6 py-4 transition-colors duration-300"
      >
        <div className="flex items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#22C55E]/30 bg-[#22C55E]/5 px-6 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#22C55E]/10">
            <Bot className="h-5 w-5 text-[#22C55E] animate-pulse" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-[#22C55E]">
              {i18n.t("v2.chat.ai.responding")}
            </span>
            <span className="text-xs text-[var(--text-secondary)]">
              {i18n.t("v2.chat.ai.disableHint")}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative border-t border-[var(--border-color)] bg-[var(--card-bg)] px-6 py-4 transition-colors duration-300"
    >
      {/* Link Preview - Aparece acima do input */}
      <AnimatePresence>
        {linkPreview && !linkPreview.error && (
          <motion.div
            initial={{ opacity: 0, y: 10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: 10, height: 0 }}
            className="mb-3 overflow-hidden"
          >
            <div className="relative rounded-xl border border-[var(--border-color)] bg-[var(--bg-tertiary)] overflow-hidden">
              {/* Botão de remover */}
              <button
                onClick={removeLinkPreview}
                className="absolute top-2 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>

              {linkPreview.loading ? (
                // Loading Skeleton
                <div className="flex animate-pulse">
                  <div className="w-24 h-24 bg-[var(--border-color)] flex-shrink-0"></div>
                  <div className="flex-1 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-[var(--border-color)]"></div>
                      <div className="h-3 bg-[var(--border-color)] rounded w-16"></div>
                    </div>
                    <div className="h-4 bg-[var(--border-color)] rounded w-3/4"></div>
                    <div className="h-3 bg-[var(--border-color)] rounded w-full"></div>
                  </div>
                </div>
              ) : (
                // Preview Carregado
                <div className="flex">
                  {/* Imagem */}
                  {linkPreview.image && (
                    <div className="w-24 h-24 flex-shrink-0 overflow-hidden bg-[var(--bg-secondary)]">
                      <img
                        src={linkPreview.image}
                        alt={linkPreview.title || 'Preview'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Conteúdo */}
                  <div className="flex-1 p-3 min-w-0">
                    {/* Site name */}
                    {linkPreview.siteName && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <Globe className="h-3 w-3 text-[var(--text-muted)]" />
                        <span className="text-xs text-[var(--text-muted)] uppercase tracking-wide">
                          {linkPreview.siteName}
                        </span>
                      </div>
                    )}
                    
                    {/* Título */}
                    {linkPreview.title && (
                      <p className="text-sm font-medium text-[var(--text-primary)] line-clamp-1">
                        {linkPreview.title}
                      </p>
                    )}
                    
                    {/* Descrição */}
                    {linkPreview.description && (
                      <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">
                        {linkPreview.description}
                      </p>
                    )}
                    
                    {/* URL */}
                    <p className="text-xs text-[#2563EB] mt-1.5 truncate flex items-center gap-1">
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      {linkPreview.url}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Attachment Menu */}
      <AnimatePresence>
        {showAttachMenu && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute bottom-full left-6 mb-2 flex items-center gap-2 rounded-2xl bg-[var(--card-bg)] p-2 shadow-lg border border-[var(--border-color)]"
          >
            {attachmentOptions.map((option) => (
              <button
                key={option.label}
                className="flex flex-col items-center gap-1 rounded-xl p-3 transition-colors hover:bg-[var(--bg-tertiary)]"
                onClick={() => setShowAttachMenu(false)}
              >
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${option.color}15` }}
                >
                  <option.icon className="h-5 w-5" style={{ color: option.color }} />
                </div>
                <span className="text-xs text-[var(--text-secondary)]">{option.label}</span>
              </button>
            ))}
            <button
              onClick={() => setShowAttachMenu(false)}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
            >
              <X className="h-3 w-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Emoji Picker */}
      <AnimatePresence>
        {showEmojiPicker && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute bottom-full left-6 mb-2 rounded-2xl bg-[var(--card-bg)] p-3 shadow-lg border border-[var(--border-color)]"
          >
            <p className="mb-2 text-xs font-medium text-[var(--text-secondary)]">{i18n.t("v2.messageInput.quickEmojis")}</p>
            <div className="flex gap-2">
              {quickEmojis.map((emoji) => (
                <button
                  key={emoji}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-colors hover:bg-[var(--bg-tertiary)]"
                  onClick={() => {
                    setMessage((prev) => prev + emoji);
                    setShowEmojiPicker(false);
                    inputRef.current?.focus();
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowEmojiPicker(false)}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
            >
              <X className="h-3 w-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit} className="flex items-end gap-3">
        {/* Emoji Button */}
        <button
          type="button"
          onClick={() => {
            setShowEmojiPicker(!showEmojiPicker);
            setShowAttachMenu(false);
          }}
          className={cn(
            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-colors",
            showEmojiPicker
              ? "bg-[#2563EB] text-white"
              : "bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
          )}
        >
          <Smile className="h-5 w-5" />
        </button>

        {/* Attachment Button */}
        <button
          type="button"
          onClick={() => {
            setShowAttachMenu(!showAttachMenu);
            setShowEmojiPicker(false);
          }}
          className={cn(
            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-colors",
            showAttachMenu
              ? "bg-[#2563EB] text-white"
              : "bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
          )}
        >
          <Paperclip className="h-5 w-5" />
        </button>

        {/* Text Input */}
        <div className="relative flex-1">
          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={i18n.t("v2.messageInput.placeholder")}
            rows={1}
            className="max-h-32 min-h-[44px] w-full resize-none rounded-2xl border border-[var(--border-color)] bg-[var(--input-bg)] px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-all focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
            style={{
              height: "auto",
              minHeight: "44px",
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto";
              target.style.height = `${Math.min(target.scrollHeight, 128)}px`;
            }}
          />
        </div>

        {/* Send / Record Button */}
        {message.trim() ? (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            type="submit"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#2563EB] text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-[#1E40AF]"
          >
            <Send className="h-5 w-5" />
          </motion.button>
        ) : (
          <button
            type="button"
            onClick={() => setIsRecording(!isRecording)}
            className={cn(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-all",
              isRecording
                ? "bg-[#EF4444] text-white animate-pulse"
                : "bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
            )}
          >
            <Mic className="h-5 w-5" />
          </button>
        )}
      </form>

      {/* Recording Indicator */}
      <AnimatePresence>
        {isRecording && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute inset-x-0 bottom-full left-0 flex items-center justify-center gap-3 bg-red-50 dark:bg-red-500/10 py-3"
          >
            <div className="h-3 w-3 animate-pulse rounded-full bg-[#EF4444]" />
            <span className="text-sm font-medium text-[#EF4444]">{i18n.t("v2.messageInput.recording")}</span>
            <span className="text-sm text-[#EF4444]">0:12</span>
            <button
              onClick={() => setIsRecording(false)}
              className="ml-4 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              {i18n.t("v2.messageInput.cancel")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}